import EditorBase from "./EditorBase.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { defaultCellRenderer, openSwivuAssistPopup } from "../../../other/utils/utils.jsx";
import SwivuTabBase from "./SwivuTabBase.jsx";
import { getTabSelectionPath } from "../../../other/utils/otherUtils.jsx";

class MainEditorBase extends EditorBase {
  static get $name() {
    return "MainEditorBase";
  }

  static get configurable() {
    return {
      listeners: {
        hide: "swivuCallOnHideOnTabItems"
      },
    }
  }

  displayRecordMetaInformation(record) {
    const bbar = this.widgetMap.bbar;
    if (bbar && bbar.getWidgetByRef("dateMetaDataField")) {
      const dateMetaDataField = bbar?.widgetMap?.dateMetaDataField;
      const { createdAt, creatorRecord, updatedAt, updaterRecord, lastEventUpdatedByRecord, lastEventUpdatedAt } = record;

      if ((record.isPhantom || !dateMetaDataField || (!creatorRecord && !updaterRecord))) {
        bbar.hide();
      }

      if (dateMetaDataField && !record.isPhantom) {
        const dateFormat = "M/D/YYYY";
        const commonCls = "overflow-hidden text-ellipsis hover:overflow-visible";
        dateMetaDataField.html = `
        <div class="overflow-hidden font-normal flex gap-3">
          ${ creatorRecord ? `<span class="${commonCls}">Created by ${defaultCellRenderer({ value: creatorRecord?.name })} on ${DateHelper.format(new Date(createdAt), dateFormat)}</span> | `: "" }
          ${ updaterRecord ? `<span class="${commonCls}">General data changed by ${defaultCellRenderer({ value: updaterRecord?.name })} on ${DateHelper.format(new Date(updatedAt), dateFormat)}</span>`: "" }
          ${ lastEventUpdatedByRecord ? ` | <span class="${commonCls}">Last scheduled by ${defaultCellRenderer({ value: lastEventUpdatedByRecord.name })} on ${DateHelper.format(new Date(lastEventUpdatedAt), dateFormat)}</span>`: "" }
        </div>
        `;
        // The bbar is set to hidden when editor is first shown since record is phantom. if record is set to a non phantom record by duplicate entity helper then bbar is still hidden. therefore, we are setting bbar to show here.
        bbar.show();
      }
    }
  }

  updateEditorTitle(title) {
    this.title = title;
  }

  editRecord(record, context, otherParams) {
    this.context = context;
    this.otherParams = otherParams;
    if (record.archived) this.swivuSetTitleLineThrough();
    // if `selectTabByTypeOnOpen` has a value then, switch current tab to given tab type
    // There's a scenario if the editor is opened via deep link or clicking on any icon that opens a specific tab (reminder icon in main grid)
    // Whenever a new editor is opened old editor is redirected back to the tab that was opened via deep link or opened based on reminder icon etc.
    // For now, we are restricting tab switch if the editor is already opened


    const { selectTabByTypeOnOpen, selectTabByDeepLinkSlugOnOpen } = otherParams;

    if (selectTabByTypeOnOpen || selectTabByDeepLinkSlugOnOpen) {
      this.widgetMap.tabs.load(record, context, {...otherParams, preventTabChange: true});
      const filterFn = selectTabByTypeOnOpen
        ? (tab) => tab.type === selectTabByTypeOnOpen
        : (tab) => tab.swivuDeepLinkSlug === selectTabByDeepLinkSlugOnOpen;
      const tabPaths = getTabSelectionPath(this.widgetMap.tabs, filterFn);
      tabPaths.forEach(({ tabPanel, tabIndex }) => tabPanel.activeTab = tabIndex);
      // We are deleting the selectTabByTypeOnOpen and selectTabByDeepLinkSlugOnOpen from the react state to prevent the tab from being switched again when the state update causes rerender.
      /* Using an if check here to prevent crash because some editors like user editor don't have react state */
      if (this.reactState) {
        delete this.reactState.record.selectTabByTypeOnOpen;
        delete this.reactState.record.selectTabByDeepLinkSlugOnOpen;
      }
    } else {
      this.widgetMap.tabs.load(record, context, otherParams);
    }
    super.editRecord(record, context, otherParams);
  }

  doPhantomRecordHandling(record) {
    if (!this.swivuDisabledTabItems) {
      this.swivuDisabledTabItems = [];
    }
    if (record.isPhantom) {
      const editorTabs = this.getWidgetByRef("tabs");
      editorTabs.items.forEach((tabItem) => {
        if (tabItem.swivuDisableOnPhantomRecord && !(tabItem.disabled && tabItem.hidden)) {
          this.swivuDisabledTabItems.push(tabItem);
          tabItem.disabled = true;
        }
      });
    } else if (this.swivuDisabledTabItems) {
      this.swivuDisabledTabItems.forEach((tabItem) => {
        tabItem.disabled = false;
      })
      delete this.swivuDisabledTabItems;
    }
    this.displayRecordMetaInformation(record);
  }

  doConfigureEditor(record, context, configOptions) {
    super.doConfigureEditor(record, context, configOptions);
    this.doPhantomRecordHandling(record);
  }

  swivuCallOnHideOnTabItems(e) {
    this.destroyTabCheckListener();
    this.widgetMap.tabs.eachWidget((widget) => {
      if (widget.isSwivuTabBase) {
        widget.onSwivuEditorHide(e);
      }
    }, false);
  }

  onClickSaveBtn({ source }) {
    const swivuTabContainer = source.up(SwivuTabBase.type, true);
    const { record } = swivuTabContainer
    swivuTabContainer.eachWidget((widget) => {
      if (widget.name && record.getFieldDefinition(widget.name)) {
        record[widget.name] = widget.value;
      }
    })
  }

  onActiveUsers(activeUsers) {
    const { generalTab } = this.widgetMap;
    if (generalTab?.onActiveUserUpdate) {
      generalTab.onActiveUserUpdate(activeUsers);
    }
  }

  updateReactState(record) {
    if (!this.reactState) throw new Error("React State is not defined for this editor. Please assign react state to this editor");
    this.reactState.record[this.reactState.recordKey] = record;
    this.reactState.recordIdObj = record;
  }

  onSwivuAssistButtonClick() {
    openSwivuAssistPopup({ editor: this, swivuAssistanceConfig: this.swivuActiveTab.swivuAssistance });
  }

  toggleSwivuAssistButton({ activeTab }) {
    this.swivuActiveTab = activeTab;
    this.widgetMap.swivuAssistButton.hidden = !activeTab.swivuAssistance
  }
}

export default MainEditorBase;
